import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, Fab, Tooltip } from '@mui/material';
import { SmartToy } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PatientPortalContent from '../portals/patient/PatientPortalContent';
import TrialParticipationPlanning from '../portals/patient/pages/TrialParticipationPlanning';
import PatientQuickAccessPage from '../portals/patient/pages/PatientQuickAccessPage';
import PlanWorkspacePage from '../shared/components/planning/PlanWorkspacePage';
import PlanItemPage from '../shared/components/planning/PlanItemPage';
import TrialPlanCreationWizardPage from '../shared/components/planning/TrialPlanCreationWizardPage';
import MyPlansPage from '../shared/components/planning/MyPlansPage';
import ClinicalPortalContent from '../portals/clinical/ClinicalPortalContent';
import CommunityPortalContent from '../portals/community/CommunityPortalContent';
import HealthcarePortalContent from '../portals/healthcare/HealthcarePortalContent';

// Using Grid system - no fixed width needed

const MainPanel = ({ user, onOpenChat }) => {
  const { getUserCategory } = useAuth();
  const location = useLocation();

  const category = getUserCategory();
  const selectedTrialId = localStorage.getItem('selectedTrialId');

  const getPortalTabs = () => {
    switch (category) {
      case 'patient_family':
        return [
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Clinical Profile', path: '/clinical-profile' },
          { label: 'Find Trials', path: '/find-trials' },
          { label: 'Support Resources', path: '/support' }
        ];
      case 'trials_team':
        return [
          { label: 'Workflow', path: '/dashboard' },
          { label: 'Trial Setup', path: '/trial-management' },
          { label: 'Patient Connect', path: '/patient-pipeline' },
          { label: 'Recruitment Metrics', path: '/analytics' },
          { label: 'Manager Review Board (AI Drafts)', path: '/manager-review' }
        ];
      case 'community_resources':
        return [
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Resource Directory', path: '/resource-directory' },
          { label: 'Case Management', path: '/case-management' },
          { label: 'Network', path: '/network' }
        ];
      case 'patient_team':
        return [
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Institution', path: '/institution' },
          { label: 'Partnerships', path: '/partnerships' },
          { label: 'Compliance', path: '/compliance' }
        ];
      default:
        return [{ label: 'Dashboard', path: '/dashboard' }];
    }
  };

  const tabs = getPortalTabs();
  const currentTabIndex = tabs.findIndex(tab => tab.path === location.pathname);

  const renderPortalContent = (tabOverride = currentTabIndex >= 0 ? currentTabIndex : 0) => {
    switch (category) {
      case 'patient_family':
        return <PatientPortalContent user={user} activeTab={tabOverride} />;
      case 'trials_team':
        return <ClinicalPortalContent user={user} activeTab={tabOverride} />;
      case 'community_resources':
        return <CommunityPortalContent user={user} activeTab={tabOverride} />;
      case 'patient_team':
        return <HealthcarePortalContent user={user} activeTab={tabOverride} />;
      default:
        return <div>Portal content not available</div>;
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%', // Fill the grid space
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      {/* Portal Content Area */}
      <Box sx={{ flexGrow: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }}>
        <Routes>
          <Route path="/dashboard" element={renderPortalContent()} />
          <Route path="/clinical-profile" element={renderPortalContent()} />
          <Route path="/find-trials" element={renderPortalContent()} />
          <Route path="/patient-events" element={category === 'patient_family' ? <PatientQuickAccessPage section="events" /> : renderPortalContent()} />
          <Route path="/patient-alerts" element={category === 'patient_family' ? <PatientQuickAccessPage section="alerts" /> : renderPortalContent()} />
          <Route path="/patient-invites" element={category === 'patient_family' ? <PatientQuickAccessPage section="invites" /> : renderPortalContent()} />
          <Route path="/trial-planning/:trialId" element={category === 'patient_family' ? <TrialParticipationPlanning /> : renderPortalContent()} />
          <Route path="/plans" element={<MyPlansPage />} />
          <Route path="/plans/new" element={<TrialPlanCreationWizardPage />} />
          <Route path="/plans/:planId/edit" element={<TrialPlanCreationWizardPage />} />
          <Route path="/plans/:planId" element={<PlanWorkspacePage />} />
          <Route path="/plans/:planId/items/:itemType/:itemId" element={<PlanItemPage />} />
          <Route path="/support" element={renderPortalContent()} />
          <Route path="/trial-management" element={renderPortalContent()} />
          <Route path="/trial-home/:trialId" element={renderPortalContent()} />
          <Route path="/patient-pipeline" element={renderPortalContent()} />
          <Route path="/analytics" element={renderPortalContent()} />
          <Route path="/manager-review" element={renderPortalContent()} />
          <Route path="/notifications" element={category === 'trials_team' ? renderPortalContent(5) : renderPortalContent()} />
          <Route path="/resource-directory" element={renderPortalContent()} />
          <Route path="/case-management" element={renderPortalContent()} />
          <Route path="/network" element={renderPortalContent()} />
          <Route path="/institution" element={renderPortalContent()} />
          <Route path="/partnerships" element={renderPortalContent()} />
          <Route path="/compliance" element={renderPortalContent()} />
          <Route path="/" element={renderPortalContent()} />
        </Routes>
      </Box>

      {/* Floating MammoAssist Chat Button */}
      <Tooltip title="Open MammoAssist AI Chat" placement="left">
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme => theme.zIndex.modal + 1,
          }}
          onClick={() => onOpenChat && onOpenChat()}
        >
          <SmartToy />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default MainPanel;